import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CATEGORIES, LOGO_SRC } from "../../data/menu";
import { APP } from "../../constants/testIds";
import { scrollToId } from "../../hooks/useLenisScroll";

export const CategoryNav = () => {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(e.target.id);
          }
        });
      },
      {
        rootMargin: "-40% 0px -55% 0px",
        threshold: 0,
      }
    );
    CATEGORIES.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) observer.observe(el);
    });

    const onScroll = () => setStuck(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <nav
      data-testid={APP.categoryNav}
      aria-label="Categorías del menú"
      className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
        stuck
          ? "bg-[#0a0906]/92 backdrop-blur-md border-b border-[#c9a961]/15"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-8">
        {/* Micro-logo — appears once stuck */}
        <motion.a
          href="#inicio"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          initial={false}
          animate={{ opacity: stuck ? 1 : 0, width: stuck ? "auto" : 0 }}
          transition={{ duration: 0.3 }}
          className="hidden md:block shrink-0 overflow-hidden"
          aria-label="Volver al inicio"
        >
          <img
            src={LOGO_SRC}
            alt=""
            className="h-8 w-auto object-contain"
            draggable={false}
          />
        </motion.a>

        <div className="relative flex-1 overflow-hidden">
          <div
            className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-[#0a0906] to-transparent z-10"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-[#0a0906] to-transparent z-10"
            aria-hidden
          />
          <ul className="flex snap-x snap-mandatory gap-1 overflow-x-auto no-scrollbar px-2">
            {CATEGORIES.map((c) => {
              const isActive = active === c.id;
              return (
                <li key={c.id} className="snap-start shrink-0">
                  <button
                    data-testid={APP.categoryNavItem(c.id)}
                    onClick={() => scrollToId(c.id, -72)}
                    className={`group relative flex items-baseline gap-2 whitespace-nowrap rounded-full px-4 py-2 font-inter text-[11px] uppercase tracking-[0.22em] transition-colors ${
                      isActive
                        ? "text-[#f4e9d5]"
                        : "text-[#f4e9d5]/55 hover:text-[#f4e9d5]/90"
                    }`}
                    aria-current={isActive ? "true" : undefined}
                  >
                    <span className="text-[9px] text-[#c9a961]/80">
                      {c.number}
                    </span>
                    <span>{c.title}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                        className="absolute inset-0 -z-10 rounded-full border border-[#c9a961]/40 bg-[#c9a961]/[0.06]"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
};
